import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { PostOptionsComponent } from '../components/post-options/post-options.component';
import { OnDestroyComponent } from '../../../shared/components/on-destroy/on-destroy.component';
import { AutoUnsubscribe } from '../../../utils/misc/auto-unsubscribe';
import { DisplayPost } from '../../../shared/models/publication';
import { ToastrService } from '../../../shared/services/utilities/toastr.service';
import { UserStoreService } from '../../../shared/services/state-management/user-store.service';
import { filterUndefined, User } from '@adeo/ngx-kozikaza-api';
import { Favorite, PostType, } from '@adeo/ngx-kozikaza-api/post';
import {filter, take, tap} from 'rxjs/operators';
import { KazaService as LegacyKazaService } from '../../../shared/services/features/kaza/kaza.service';
import { PostsFluxService } from '../../../shared/components/posts-flux/posts-flux.service';
import { GoogleTranslateService } from '../../../shared/services/translation/google-translate.service';
import { catchError } from 'rxjs/internal/operators/catchError';
import { throwError } from 'rxjs/internal/observable/throwError';
import {Subject} from "rxjs/internal/Subject";
import { kzIconsPostQuestion } from 'src/app/kz-ui/ui/icon/generated-icons/kzIcons-post-question.icon';
import { KzIconsRegistryService } from 'src/app/kz-ui/ui/icon/kz-icons-registry.service';

@Component({
  selector: 'app-post-preview',
  templateUrl: './post-preview.component.html',
  styleUrls: ['./post-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe()
export class PostPreviewComponent extends OnDestroyComponent implements OnInit, AfterViewInit {

  @Input() post: DisplayPost;
  public postTypePicture = PostType.Pictures;
  public postTypeIdea = PostType.Idea;
  public user: User;
  public userId = '';
  public kazaId = '';

  private subscriptionTranslation: Subscription;

  public userLogged: User;
  public userLoggedIsPostAuthor = false;
  public userLoggedIsAdmin = false;
  public initSurveyForm = 0;

  public maxHeight = 68;
  public text = '';
  public textMemory = '';
  public isCollapsed = false;

  private _postDescriptionLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public descriptionTranslatedDisplayed = false;
  public descriptionTranslatedNbLines = 3;
  public descriptionMemoryTranslatedNbLines = 3;
  public canBeTranslated = false;
  public alreadyTranslated = false;

  public isPostTypeEvent: boolean;
  public isPostTypeQuestion: boolean
  public isPostTypeSurvey: boolean
  public isPostTypeVideo: boolean;
  public isPostType360: boolean;

  private _updateCommentList: BehaviorSubject<number> = new BehaviorSubject(0);
  public updateCommentList: Observable<number> = this._updateCommentList.asObservable().pipe(
    filter((cpt) => cpt > 0),
  );

  public today = new Date();

  @ViewChild('kzPost', {static: false})
  private kzPostComponent: ElementRef;

  @ViewChild(PostOptionsComponent, {static: false}) postOptionsComponent: PostOptionsComponent;

  @ViewChild('container', {static: false}) container;

  @Output() removedPost: EventEmitter<string> = new EventEmitter<string>();
  @Output() updatedPost: EventEmitter<DisplayPost> = new EventEmitter<DisplayPost>();

  @Output() favoriteAdded: EventEmitter<Favorite> = new EventEmitter<Favorite>();
  @Output() favoriteRemoved: EventEmitter<string> = new EventEmitter<string>();

  constructor(
    public readonly userStoreService: UserStoreService,
    protected readonly kazaService: LegacyKazaService,
    protected readonly toastr: ToastrService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly postsFluxService: PostsFluxService,
    protected readonly googleTranslateService: GoogleTranslateService,
    protected readonly kzIconsRegistryService: KzIconsRegistryService
  ) {
    super();
    this.kzIconsRegistryService.registerIcons([kzIconsPostQuestion]);
  }

  public get postDescriptionLoading$(): Observable<boolean> {
    return this._postDescriptionLoadingSubject.asObservable();
  }

  ngOnInit() {
    this.canBeTranslated = this.googleTranslateService.canBeTranslated(this.post);
    // define nb lines for description placeHolder
    this.descriptionTranslatedNbLines = (this.post.description) ? (Math.ceil(this.post.description.length / 55) + 1) : 0;
    this.userStoreService.userStore.pipe(
      filterUndefined(),
      take(1),
      tap((u) => {
        this.userLogged = u;
        this.userLoggedIsPostAuthor = (typeof this.post.author === 'string' ? this.post.author : this.post.author['@id']) === ((u) ? u['@id'] : null);
        this.userLoggedIsAdmin = this.userStoreService.isAdmin;
        this.cdr.markForCheck();
      }),
    ).subscribe();
    this.user = typeof this.post.author !== 'string' ? this.post.author : null;
    // this.kaza = typeof this.post.kaza !== 'string' ? this.post.kaza : null;
    this.userId = typeof this.post.author !== 'string' ? this.post.author['@id'] : this.post.author;
    // this.kazaId = this.kaza['@id'];
    this.kazaId = typeof this.post.kaza !== 'string' ? this.post.kaza['@id'] : this.post.kaza;
    this.isPostTypeEvent = this.checkType(this.post.postMemory || this.post,PostType.Event) && !!this.post.event;
    this.isPostTypeQuestion = this.checkType(this.post.postMemory || this.post, PostType.Question);
    this.isPostTypeSurvey= this.checkType(this.post.postMemory || this.post, PostType.Survey);
    this.isPostTypeVideo = this.checkType(this.post.postMemory || this.post, PostType.Video);
    this.finalizePostDescriptionTranslation();
    this.cdr.markForCheck();
  }

  private checkType(post, type: PostType): boolean {
    return post.type === type;
  }

  private finalizePostDescriptionTranslation() {
    this.updateDescription();
    this._postDescriptionLoadingSubject.next(false);
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.isCollapsed = this.container?.nativeElement.offsetHeight > this.maxHeight;
    this.cdr.detectChanges();
    // Since we know the list is not going to change
    // let's request that this component not undergo change detection at all
    this.cdr.detach();
  }

  toggleView() {
    this.isCollapsed = false;
    this.cdr.detectChanges();
  }

  public hasBeenDeleted(postId: string): void {
    this.removedPost.emit(postId);
  }

  public openPost(post, isPostMemory = false) {
    this.postsFluxService.openPost(post['@id'], {
      queryParamsHandling: 'preserve'
    })
    .pipe(
      take(1),
      tap((post) => this._updateCommentList.next(this._updateCommentList.value + 1)),
      tap((post) => this.updatePost(post, isPostMemory))
    ).subscribe();
  }

  public openPostMemoryShare() {
    this.postsFluxService.openPostMemoryShare(this.post['@id'], {
      queryParamsHandling: 'preserve'
    }).pipe(
      take(1),
      tap((post) => this.updatePost(post))
    ).subscribe();
  }

  public updatePost(post: DisplayPost, isPostMemory = false) {
    // hide option menu if still opened
    if (this.postOptionsComponent) {
      this.postOptionsComponent.optionMenuHide();
    }

    if (isPostMemory) {
      this.post.postMemory = post;
    } else {
      this.post = post;
    }

    if (this.descriptionTranslatedDisplayed) {
      this.alreadyTranslated = false;
      this.translatePost();
    } else {
      this.updateDescription();
    }
  }

  private translatePost() {
    if (!this.alreadyTranslated) {
      this._postDescriptionLoadingSubject.next(true);
      this.subscriptionTranslation = this.googleTranslateService.handlePostTranslation(this.post).pipe(
        take(1),
        catchError((err) => {
          this.finalizePostDescriptionTranslation();
          return throwError(err);
        }),
        tap((post) => {
          this.post = post;
          this.alreadyTranslated = true;
          this.finalizePostDescriptionTranslation();
        }),
      ).subscribe();
    }
  }

  public translateDescription(translateToCurrentLanguage = true) {
    this.descriptionTranslatedDisplayed = translateToCurrentLanguage;
    if (this.alreadyTranslated) {
      this.updateDescription();
    } else {
      this.translatePost();
    }
  }

  private updateDescription() {
    const description = (this.descriptionTranslatedDisplayed && this.post.translatedDescription) ? this.post.translatedDescription : this.post.description;
    const postMemory = this.post.postMemory as DisplayPost;

    this.text = ((this.post.firstTimeHashtag) ? (this.post.firstTimeHashtag + ' ' + description) : description);

    if (postMemory) {
      const descriptionMemory = (this.descriptionTranslatedDisplayed && postMemory && postMemory.translatedDescription) ? postMemory.translatedDescription : postMemory.description;
      this.textMemory = ((postMemory && postMemory.firstTimeHashtag) ? (postMemory.firstTimeHashtag + ' ' + descriptionMemory) : descriptionMemory);
    }
    this.cdr.detectChanges();
  }

}
